"use client";

import { useState } from "react";
import {
  ArrowRightIcon,
  InfoIcon,
  MoreHorizontalIcon,
  PlayIcon,
  TrashIcon,
} from "lucide-react";

import { createPath } from "@/service/routing/routing";
import { cn } from "@/lib/utils";
import { ExamTypeEnum } from "@/types/exams-enums";
import { clientDel } from "@/service/client-api/client-api";
import { SuperLink as Link } from "@/components/common/super-link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PictureAsset } from "@/components/common/picture-asset";
import { DeleteModal } from "@/components/common/delete-modal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getAssetImage } from "@/service/image/asset";

// Srebrna plakietka z rokiem. Gradient jedzie zmienną z fallbackiem na dotychczasowy jasny wariant -
// w trybie ciemnym egzaminów biała plakietka zjadała własny (rozjaśniony) tekst.
const yearBadgeGradient =
  "var(--exam-year-badge-gradient, linear-gradient(120deg, rgb(230,230,230) 24%, rgb(255,255,255) 59%, rgb(153,153,153) 99%))";

const StatusBadge = ({
  isFinished,
  passed,
}: {
  isFinished: boolean;
  passed: boolean;
}) => {
  if (!isFinished) {
    return (
      <Badge className="border-transparent bg-cyan-100 text-cyan-800">
        w trakcie
      </Badge>
    );
  }

  if (passed) {
    return (
      <Badge className="border-transparent bg-teal-100 text-teal-800">
        zdane
      </Badge>
    );
  }

  return (
    <Badge className="border-transparent bg-rose-100 text-rose-800">
      niezdane
    </Badge>
  );
};

export const ExamAttemptCard = ({
  attempt,
  fluid = false,
}: {
  attempt: ExamAttemptCardVM;
  examType: ExamTypeEnum;
  fluid?: boolean;
}) => {
  const {
    id,
    exam,
    activeQuestion,
    questionsCount,
    answersCount,
    finalScore,
    fullName,
    passed,
  } = attempt;

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const isFinished = finalScore !== null;

  // Egzaminy próbne mają własne grafiki rund (probny-runda-N) — numer rundy bierzemy z nazwy,
  // np. „Próbny egzamin LEK - jesień 2026, runda 1". Reszta zestawów używa illustrationKey.
  const practiceRound = fullName.startsWith("Próbny egzamin")
    ? fullName.match(/runda\s+(\d+)/i)?.[1] ?? "1"
    : null;
  const imageSource = getAssetImage(
    practiceRound
      ? `/exams/illustrations/probny-runda-${practiceRound}`
      : `/exams/illustrations/${attempt.illustrationKey}`,
  );

  const path = !isFinished
    ? createPath("exam.attempt.question", {
        attempt: id,
        questionNumber: activeQuestion + 1,
      })
    : createPath("exam.attempt.result", { attempt: id });

  return (
    <div
      className={cn(
        "flex flex-col gap-4 rounded-lg border border-zinc-100 bg-white p-3 shadow-[4px_8px_19px_0px_rgba(6,182,212,0.15)]",
        fluid ? "w-full" : "w-[340px] shrink-0",
      )}
    >
      <div className="flex w-full flex-col gap-2">
        {/* Grafika + rok (grafiki jeszcze nie mamy) */}
        <div className="relative h-[105px] w-full overflow-hidden rounded-[5px] bg-zinc-100">
          <PictureAsset
            source={imageSource}
            ext="png"
            alt={fullName}
            className="h-full w-full object-cover"
            wrapperClassName="block h-full w-full"
          />
          {exam.year && (
            <span
              // Biała obwódka jest obliczona pod jasną plakietkę; na ciemnej świeciłaby obręczą,
              // dlatego w trybie ciemnym schodzi do szarości (patrz tailwind-styles/exam-dark.css).
              className="absolute top-2 right-2 rounded-[17px] border border-white px-2 py-0.5 text-xs font-medium text-black dark:border-zinc-600"
              style={{ backgroundImage: yearBadgeGradient }}
            >
              {exam.year}
            </span>
          )}
          {isFinished && (
            <span
              className={`absolute top-2 left-2 rounded-[17px] px-2 py-0.5 text-xs font-semibold ${
                passed
                  ? "bg-teal-100 text-teal-800"
                  : "bg-rose-100 text-rose-800"
              }`}
            >
              {finalScore}%
            </span>
          )}
        </div>

        {/* Tytuł + status + menu */}
        <div className="flex w-full flex-col">
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <h3
                data-title={fullName}
                className="text-card-foreground truncate text-base font-semibold"
              >
                {fullName}
              </h3>
              <StatusBadge isFinished={isFinished} passed={passed} />
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Akcje egzaminu"
                  className="text-muted-foreground hover:text-foreground size-8 shrink-0"
                >
                  <MoreHorizontalIcon size={16} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link href={path}>
                    {isFinished ? (
                      <InfoIcon size={16} />
                    ) : (
                      <PlayIcon size={16} />
                    )}
                    {isFinished ? "Szczegóły" : "Kontynuuj"}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => {
                    setTimeout(() => setIsDeleteOpen(true), 0);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <TrashIcon size={16} className="text-red-600" />
                  Usuń
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-muted-foreground text-sm font-medium">
            {answersCount}/{questionsCount} pytań
          </p>
        </div>
      </div>

      <Button variant="cyan" asChild className="w-full">
        <Link href={path}>
          {isFinished ? "Szczegóły" : "Kontynuuj"}
          <ArrowRightIcon />
        </Link>
      </Button>

      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onDelete={() => {
          clientDel("exam.delete.attempt", { attempt: id }).then(() => {
            window.location.reload();
          });
        }}
        resourceName="egzamin"
      />
    </div>
  );
};
